/**
 * CR332: Flyer Evidence Section
 * Shows a thumbnail of the flyer page where a deal was found,
 * with a modal to view the full image.
 * Admin-only, used in DealDetailsContent.
 * 
 * CR335: Refactored to use shared useSignedStorageUrl hook.
 */

import { useState } from "react";
import { FileImage, ZoomIn, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useSignedStorageUrl } from "@/hooks/useSignedStorageUrl";
import type { FlyerArtifact } from "@/hooks/useAdminPreviewDeals";

interface FlyerEvidenceSectionProps {
  flyerArtifact: FlyerArtifact;
}

export function FlyerEvidenceSection({ flyerArtifact }: FlyerEvidenceSectionProps) {
  const [modalOpen, setModalOpen] = useState(false);
  // CR416: Prefer thumbnail_storage_path > snippet_asset_path > page_image_path
  const thumbnailPath = flyerArtifact.thumbnail_storage_path;
  const snippetPath = flyerArtifact.snippet_asset_path;
  const pagePath = flyerArtifact.page_image_path;
  // For inline thumbnail: prefer thumbnail > snippet > page
  const thumbDisplayPath = thumbnailPath ?? snippetPath ?? pagePath;
  const { url: thumbUrl, loading: thumbLoading } = useSignedStorageUrl(thumbDisplayPath);
  // For modal full view: prefer snippet > page (skip thumbnail - it's lower res)
  const fullViewPath = snippetPath ?? pagePath;
  const needsSeparateFullUrl = fullViewPath && fullViewPath !== thumbDisplayPath;
  const { url: separateFullUrl, loading: fullLoading } = useSignedStorageUrl(needsSeparateFullUrl ? fullViewPath : null);
  
  const loading = thumbLoading || fullLoading;
  const flyerThumbUrl = thumbUrl;
  const flyerFullUrl = separateFullUrl || thumbUrl;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading flyer evidence…
      </div>
    );
  }

  if (!flyerThumbUrl) return null;

  return (
    <>
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold flex items-center gap-1.5">
            <FileImage className="w-4 h-4" />
            Flyer Evidence
          </h4>
          {flyerArtifact.page_number != null && (
            <Badge variant="outline" className="text-xs">
              Page {flyerArtifact.page_number}
            </Badge>
          )}
        </div>

        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="group relative w-full rounded-lg border bg-muted/30 overflow-hidden hover:border-primary/50 transition-colors cursor-pointer"
        >
          <div className="aspect-[4/3] w-full overflow-hidden">
            <img
              src={flyerThumbUrl}
              alt="Flyer page where deal was found"
              className="w-full h-full object-contain"
              loading="lazy"
            />
          </div>
          <div className="absolute inset-0 flex items-center justify-center bg-background/60 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="flex flex-col items-center gap-1 text-foreground">
              <ZoomIn className="h-5 w-5" />
              <span className="text-xs font-medium">View flyer</span>
            </div>
          </div>
        </button>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Flyer Evidence</DialogTitle>
            {flyerArtifact.page_number != null && (
              <DialogDescription>
                Page {flyerArtifact.page_number}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="flex items-center justify-center">
            <img
              src={flyerFullUrl || flyerThumbUrl}
              alt="Full flyer page"
              className="max-w-full max-h-[75vh] object-contain rounded-md"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
